import { NodeDetails, Operator } from '@/types';
import { getAssetStatusFilterMap } from '@/utils/entities/assetStatusQuery.util';

// Type guards for safe filter value conversion
export function isValidFilterValue(
  value: unknown
): value is Exclude<NonNullable<NodeDetails['filters']>[0]['value'], null> {
  if (value === null || value === undefined) return false;
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  )
    return true;
  if (Array.isArray(value))
    return value.every(v => v !== null && v !== undefined);
  return false;
}

export function sanitizeFilterValue(
  value: unknown
): Exclude<NonNullable<NodeDetails['filters']>[0]['value'], null> | undefined {
  if (!isValidFilterValue(value)) {
    return undefined;
  }
  return value;
}

export interface Filters {
  [key: string]: string | string[];
}

export interface UnifiedFilterResult {
  nodeFilters: NonNullable<NodeDetails['filters']>;
  relationshipFilters: NonNullable<NodeDetails['relationships']>;
  graphQuery?: {
    node: NodeDetails;
    orderBy?: string;
    descending?: boolean;
  };
}

/**
 * Unified filter transformation function that converts legacy Asset Table filters
 * using QueryBuilder's proven filter mapping logic for consistent Neo4j querying.
 *
 * This ensures both Asset Table and QueryBuilder use the same transformation logic,
 * eliminating inconsistencies like the missing AssetStatus.Pending issue.
 */
export function unifyAssetFilters(filters: Filters): UnifiedFilterResult {
  // Use the enhanced manual transformation with QueryBuilder's proven mappings
  return buildFiltersManually(filters);
}

/**
 * Manual filter transformation fallback using Asset Table's current logic
 * but enhanced with QueryBuilder's status filter mappings
 */
function buildFiltersManually(filters: Filters): UnifiedFilterResult {
  const nodeFilters: NonNullable<NodeDetails['filters']> = [];

  // Enhanced filter map that includes all status types from QueryBuilder
  const filterMap = getAssetStatusFilterMap();

  // Handle status filters with proper mapping
  if (
    filters.status &&
    Array.isArray(filters.status) &&
    filters.status.length > 0
  ) {
    filters.status.forEach((status: string) => {
      const mapping = filterMap[status];
      if (mapping) {
        if (mapping.field) {
          const sanitizedValue = sanitizeFilterValue(mapping.value);
          if (sanitizedValue !== undefined) {
            nodeFilters.push({
              field: mapping.field,
              operator: mapping.operator as Operator,
              value: sanitizedValue,
            });
          } else {
            console.warn(
              `Invalid filter value for status ${status}:`,
              mapping.value
            );
          }
        } else if (Array.isArray(mapping.value)) {
          // Handle complex status combinations
          const complexFilters = mapping.value
            .map(sf => {
              const sanitizedValue = sanitizeFilterValue(sf.value);
              return sanitizedValue !== undefined
                ? {
                    field: sf.field,
                    operator: sf.operator as Operator,
                    value: sanitizedValue,
                  }
                : null;
            })
            .filter(
              (filter): filter is NonNullable<typeof filter> => filter !== null
            );
          nodeFilters.push(...complexFilters);
        }
      }
    });
  }

  // Handle other filters
  Object.entries(filters).forEach(([key, value]) => {
    if (
      key === 'status' ||
      !value ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return;
    }

    nodeFilters.push({
      field: key,
      operator: typeof value === 'string' ? 'CONTAINS' : '=',
      value: Array.isArray(value) ? value : [value],
    });
  });

  return {
    nodeFilters,
    relationshipFilters: [],
  };
}

/**
 * Validates that the transformation produces consistent results
 * Useful for testing and debugging filter inconsistencies
 */
export function validateFilterTransformation(filters: Filters): {
  isValid: boolean;
  issues: string[];
} {
  const issues: string[] = [];

  // Check for missing status mappings
  if (filters.status && Array.isArray(filters.status)) {
    const filterMap = getAssetStatusFilterMap();
    filters.status.forEach((status: string) => {
      if (!filterMap[status]) {
        issues.push(`Missing status mapping for: ${status}`);
      }
    });
  }

  // Check for transformation consistency
  try {
    const result = unifyAssetFilters(filters);
    if (result.nodeFilters.length === 0 && Object.keys(filters).length > 0) {
      issues.push(
        'Transformation resulted in empty filters despite non-empty input'
      );
    }
  } catch (error) {
    issues.push(`Transformation error: ${error}`);
  }

  return {
    isValid: issues.length === 0,
    issues,
  };
}
